import { useEffect, useState } from "react";

import { DateRange } from "@mui/icons-material";
import { Box, Button, Popover, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import { getDateRangeText } from "../../utils/helpers.js";
import { helperGetIsoDate } from "../../utils/helpers.js";
import OmniDatePicker from "./omniDatePicker";
import OmniResponsiveButton from "./omniResponsiveButton";

const OmniDateRangePopover = ({ analyticsStartDate, analyticsEndDate, onRangeChange }) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [buttonText, setButtonText] = useState(
    getDateRangeText(analyticsStartDate, analyticsEndDate),
  );

  useEffect(() => {
    if (analyticsStartDate != null && analyticsEndDate != null) {
      setButtonText(getDateRangeText(analyticsStartDate, analyticsEndDate));
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsStartDate, analyticsEndDate]);

  const [startDate, setStartDate] = useState(analyticsStartDate);
  const [endDate, setEndDate] = useState(analyticsEndDate);

  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  const handleOpen = event => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    if (startDate && endDate) {
      onRangeChange(startDate, endDate);
      handleClose();
    }
  };

  return (
    <>
      <OmniResponsiveButton
        tooltipTitle="Select Date Range"
        buttonText={buttonText}
        icon={<DateRange />}
        onClick={handleOpen}
      />

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box
          sx={{
            p: 2,
            width: 400,
            flexDirection: "row",
            display: "flex",
            gap: 1,
            borderRadius: "8px",
            paddingBottom: 0,
          }}
        >
          <Box sx={{ width: 180, borderRight: `1px solid ${theme.palette.secondary.main}`, pr: 2 }}>
            <Stack spacing={1}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  const tmpEndDate = new Date(helperGetIsoDate(0, false, true)); //last day
                  const tmpStartDate = new Date(helperGetIsoDate(364, true, true)); //last year
                  onRangeChange(tmpStartDate, tmpEndDate);
                  handleClose();
                }}
              >
                Last Year
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  const tmpEndDate = new Date(helperGetIsoDate(0, false, true)); //last day
                  const tmpStartDate = new Date(helperGetIsoDate(179, true, true)); //last 6 months
                  onRangeChange(tmpStartDate, tmpEndDate);
                  handleClose();
                }}
              >
                Last 6 Months
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  const tmpEndDate = new Date(helperGetIsoDate(0, false, true)); //last day
                  const tmpStartDate = new Date(helperGetIsoDate(89, true, true)); //last 90 days
                  onRangeChange(tmpStartDate, tmpEndDate);
                  handleClose();
                }}
              >
                Last 90 Days
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  const tmpEndDate = new Date(helperGetIsoDate(0, false, true)); //last day
                  const tmpStartDate = new Date(helperGetIsoDate(29, true, true)); //last 30 days
                  onRangeChange(tmpStartDate, tmpEndDate);
                  handleClose();
                }}
              >
                Last 30 Days
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  const tmpEndDate = new Date(helperGetIsoDate(0, false, true)); //last day
                  const tmpStartDate = new Date(helperGetIsoDate(6, true, true)); //last 7 days
                  onRangeChange(tmpStartDate, tmpEndDate);
                  handleClose();
                }}
              >
                Last 7 Days
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  const tmpEndDate = new Date(helperGetIsoDate(0, false, true)); //last day
                  const tmpStartDate = new Date(helperGetIsoDate(1, true, true)); //last day
                  onRangeChange(tmpStartDate, tmpEndDate);
                  handleClose();
                }}
              >
                Yesterday
              </Button>
            </Stack>
          </Box>
          <Box
            sx={{
              paddingLeft: theme.spacing(1),
              paddingRight: 0,
              width: 180,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ width: "100%", textAlign: "center", marginBottom: theme.spacing(1) }}
              variant="h5"
            >
              Custom Range
            </Typography>

            <Typography
              sx={{ width: "100%", textAlign: "center", marginBottom: theme.spacing(2) }}
              variant="h6"
            >
              {getDateRangeText(startDate, endDate)}
            </Typography>
            <OmniDatePicker
              label="Start Date"
              disableFuture={true}
              value={startDate}
              name={uuid()} //prevents AutoComplete Suggestions
              variant="inline"
              inputVariant="outlined"
              onAccept={null}
              color="secondary"
              onChange={date => setStartDate(date)}
              format="MM/dd/yyyy"
              fullWidth
            />
            <Box sx={{ textAlign: "center", margin: theme.spacing(1) }}></Box>
            <OmniDatePicker
              label="End Date"
              value={endDate}
              disableFuture={true}
              color="secondary"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="inline"
              inputVariant="outlined"
              onAccept={null}
              onChange={date => setEndDate(date)}
              format="MM/dd/yyyy"
              fullWidth
            />
          </Box>
        </Box>
        <Stack
          sx={{
            margin: theme.spacing(2),
          }}
        >
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={handleApply} disabled={!startDate || !endDate}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default OmniDateRangePopover;
